import React, { useState, useEffect } from "react";
import Select from "react-select";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import styles from "../css/css_pages/InvoiceManagement.module.css";
import config from "../../config";
import Cookies from "js-cookie";
import {
  FiPlus,
  FiSearch,
  FiRefreshCw,
  FiEdit2,
  FiX,
  FiCheckCircle,
  FiCalendar,
} from "react-icons/fi";
import { format } from "date-fns";

const InvoiceManagement = () => {
  // State declarations
  const [invoices, setInvoices] = useState([]);
  const [filteredInvoices, setFilteredInvoices] = useState([]);
  const [completedInvoices, setCompletedInvoices] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [clients, setClients] = useState([]);
  const [invoiceTypes, setInvoiceTypes] = useState([]);
  const [tsos, setTsos] = useState([]);
  const [selectedTsoFilter, setSelectedTsoFilter] = useState(null);
  const [dueMonthFilter, setDueMonthFilter] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [currentInvoice, setCurrentInvoice] = useState(null);
  const [formData, setFormData] = useState({
    payment_date: "",
    payment_description: "",
    payment_amount: "",
    client_id: "",
    payment_type: "",
  });
  const [formErrors, setFormErrors] = useState({});
  const [updateStatus, setUpdateStatus] = useState({
    success: null,
    message: "",
    currentBalance: "",
    newBalance: "",
    paymentId: "",
  });
  const [showPaymentHistoryModal, setShowPaymentHistoryModal] = useState(false);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [isLoadingPayments, setIsLoadingPayments] = useState(false);
  const [paymentHistoryError, setPaymentHistoryError] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [showCompletedInvoices, setShowCompletedInvoices] = useState(false);

  // Payment type options
  const paymentTypeOptions = [
    { value: "cash", label: "Cash" },
    { value: "bank_of_ceylon", label: "Bank of Ceylon" },
    { value: "peoples_bank", label: "Peoples Bank" },
  ];

  // Fetch data from API
  const fetchData = async () => {
    setIsLoading(true);
    setIsRefreshing(true);
    setError(null);

    try {
      const token = Cookies.get("authToken");
      if (!token) throw new Error("Authentication token not found");

      const [clientsResponse, invoicesResponse, typesResponse, tsosResponse] =
        await Promise.all([
          fetch(`${config.API_URL}/management/get-all-clients`, {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }),
          fetch(`${config.API_URL}/management/get-all-invoices`, {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }),
          fetch(`${config.API_URL}/management/get-invoice-types`, {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }),
          fetch(`${config.API_URL}/management/tso-list`, {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }),
        ]);

      if (!clientsResponse.ok) throw new Error("Failed to fetch clients");
      if (!invoicesResponse.ok) throw new Error("Failed to fetch invoices");
      if (!typesResponse.ok) throw new Error("Failed to fetch invoice types");
      if (!tsosResponse.ok) throw new Error("Failed to fetch TSOs");

      const clientsData = await clientsResponse.json();
      const invoicesData = await invoicesResponse.json();
      const typesData = await typesResponse.json();
      const tsosData = await tsosResponse.json();

      console.log("TSOs data:", tsosData);
      console.log("Sample invoice:", invoicesData[0]);

      setClients(Array.isArray(clientsData) ? clientsData : []);
      setTsos(Array.isArray(tsosData) ? tsosData : []);

      // Separate invoices into active (balance > 0) and completed (balance = 0)
      const activeInvoices = Array.isArray(invoicesData)
        ? invoicesData.filter((invoice) => parseFloat(invoice.balance) > 0)
        : [];
      const completedInvoices = Array.isArray(invoicesData)
        ? invoicesData.filter((invoice) => parseFloat(invoice.balance) === 0)
        : [];

      setInvoices(activeInvoices);
      setCompletedInvoices(completedInvoices);
      setFilteredInvoices(activeInvoices);

      let typesArray = [];
      if (Array.isArray(typesData)) {
        typesArray = typesData;
      } else if (typesData && Array.isArray(typesData.invoice_types)) {
        typesArray = typesData.invoice_types;
      } else if (typesData && typesData.data && Array.isArray(typesData.data)) {
        typesArray = typesData.data;
      }
      setInvoiceTypes(typesArray);
    } catch (error) {
      console.error("Error fetching data:", error);
      setError(error.message);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  // Fetch payment history for an invoice
  const fetchPaymentHistory = async (invoiceId) => {
    setIsLoadingPayments(true);
    setPaymentHistoryError(null);

    try {
      const token = Cookies.get("authToken");
      if (!token) throw new Error("Authentication token not found");

      const response = await fetch(
        `${config.API_URL}/management/get-all-payments/${invoiceId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (!response.ok) {
        throw new Error("Failed to fetch payment history");
      }

      const data = await response.json();
      setPaymentHistory(Array.isArray(data.response) ? data.response : []);
    } catch (error) {
      console.error("Error fetching payment history:", error);
      setPaymentHistoryError(error.message);
    } finally {
      setIsLoadingPayments(false);
    }
  };

  // Initial data fetch
  useEffect(() => {
    fetchData();
  }, []);

  // Filter invoices based on selected client, TSO, due month and search term
  useEffect(() => {
    let result = showCompletedInvoices ? [...completedInvoices] : [...invoices];

    if (selectedClient && selectedClient.value !== "all") {
      result = result.filter(
        (invoice) => invoice.client_id === selectedClient.value,
      );
    }

    if (selectedTsoFilter && selectedTsoFilter.value !== "all") {
      console.log("=== TSO FILTER DEBUG ===");
      console.log("Selected TSO:", selectedTsoFilter);
      console.log("TSO Value:", selectedTsoFilter.value);
      console.log("Total invoices before filter:", result.length);

      // Log some sample invoice data
      console.log(
        "Sample invoices TSO data:",
        result.slice(0, 5).map((inv) => ({
          invoice_num: inv.invoice_num,
          created_by: inv.created_by,
          customer_tso_id: inv.customer_tso_id,
          customer: inv.customer_name,
          tso_name: inv.tso_name,
        })),
      );

      // Log all TSOs for comparison
      console.log(
        "Available TSOs:",
        tsos.map((t) => ({
          id: t.tso_id,
          name: t.tso_calling_name || t.tso_name,
        })),
      );

      // Filter by customer's TSO (the TSO shown in the table)
      result = result.filter((invoice) => {
        const matches = invoice.customer_tso_id === selectedTsoFilter.value;
        return matches;
      });

      console.log("Filtered invoices count:", result.length);
      console.log("=== END TSO FILTER DEBUG ===");
    }

    if (dueMonthFilter) {
      result = result.filter((invoice) => {
        if (!invoice.due_date) return false;
        const dueDate = new Date(invoice.due_date);
        const filterMonth = dueMonthFilter.getMonth();
        const filterYear = dueMonthFilter.getFullYear();
        return (
          dueDate.getMonth() === filterMonth &&
          dueDate.getFullYear() === filterYear
        );
      });
    }

    if (searchTerm) {
      result = result.filter((invoice) => {
        if (
          invoice.invoice_num &&
          String(invoice.invoice_num).includes(searchTerm)
        ) {
          return true;
        }

        const term = searchTerm.toLowerCase();
        return (
          String(invoice.description || "")
            .toLowerCase()
            .includes(term) ||
          String(invoice.client_name || "")
            .toLowerCase()
            .includes(term)
        );
      });
    }

    setFilteredInvoices(result);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedClient,
    selectedTsoFilter,
    dueMonthFilter,
    searchTerm,
    invoices,
    completedInvoices,
    showCompletedInvoices,
  ]);

  // Totals calculation functions
  const getTotalAmount = () => {
    return filteredInvoices.reduce((total, invoice) => {
      return total + (parseFloat(invoice.amount) || 0);
    }, 0);
  };

  const getTotalBalance = () => {
    return filteredInvoices.reduce((total, invoice) => {
      return total + (parseFloat(invoice.balance) || 0);
    }, 0);
  };

  // Navigation handlers
  const handleCreateInvoice = () => {
    window.location.href = "/invoice";
  };

  const handleUpdateInvoice = (invoice, e) => {
    e.stopPropagation();
    setCurrentInvoice(invoice);
    setFormData({
      payment_date: "",
      payment_description: "",
      payment_amount: "",
      client_id: invoice.client_id,
      payment_type: "",
    });
    setFormErrors({});
    setUpdateStatus({
      success: null,
      message: "",
      currentBalance: formatCurrency(invoice.balance || invoice.amount),
      newBalance: "",
      paymentId: "",
    });
    setShowUpdateModal(true);
  };

  const handleShowPaymentHistory = async (invoice) => {
    setCurrentInvoice(invoice);
    setShowPaymentHistoryModal(true);
    await fetchPaymentHistory(invoice.invoice_id);
  };

  const handleRefresh = () => {
    fetchData();
  };

  const handleCloseModal = () => {
    setShowUpdateModal(false);
    setShowPaymentHistoryModal(false);
    setCurrentInvoice(null);
    setPaymentHistory([]);
  };

  const toggleCompletedInvoices = () => {
    setShowCompletedInvoices(!showCompletedInvoices);
    setSelectedClient(null);
    setSearchTerm("");
  };

  // Form handlers
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (formErrors[name]) {
      setFormErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleSelectChange = (name, selectedOption) => {
    setFormData((prev) => ({
      ...prev,
      [name]: selectedOption.value,
    }));

    if (formErrors[name]) {
      setFormErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  // Form validation
  const validateForm = () => {
    const errors = {};
    if (!formData.payment_date)
      errors.payment_date = "Payment date is required";

    if (!formData.payment_type)
      errors.payment_type = "Payment type is required";

    if (!formData.payment_amount) {
      errors.payment_amount = "Payment amount is required";
    } else {
      const paymentAmount = parseFloat(formData.payment_amount);
      const invoiceBalance = parseFloat(
        currentInvoice.balance || currentInvoice.amount,
      );

      if (isNaN(paymentAmount)) {
        errors.payment_amount = "Payment amount must be a number";
      } else if (paymentAmount <= 0) {
        errors.payment_amount = "Payment amount must be greater than 0";
      } else if (paymentAmount > invoiceBalance) {
        errors.payment_amount = `Payment amount exceeds invoice balance (Max: ${formatCurrency(
          invoiceBalance,
        )})`;
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsUpdating(true);

    try {
      const token = Cookies.get("authToken");
      if (!token) throw new Error("Authentication token not found");

      const paymentAmount = parseFloat(formData.payment_amount);
      const invoiceBalance = parseFloat(
        currentInvoice.balance || currentInvoice.amount,
      );

      if (paymentAmount > invoiceBalance) {
        throw new Error(`Payment amount exceeds invoice balance`);
      }

      const paymentDateTime = `${formData.payment_date} 00:00:00`;

      const response = await fetch(
        `${config.API_URL}/management/update-invoice-values/${currentInvoice.invoice_id}`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            payment_date: paymentDateTime,
            payment_description: formData.payment_description,
            payment_amount: paymentAmount,
            client_id: currentInvoice.client_id,
            payment_type: formData.payment_type,
          }),
        },
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to update invoice");
      }

      const responseData = await response.json();

      const originalBalance = parseFloat(
        currentInvoice.balance || currentInvoice.amount,
      );

      setUpdateStatus({
        success: true,
        message:
          responseData.message ||
          "Payment recorded and invoice updated successfully",
        currentBalance: formatCurrency(originalBalance),
        newBalance: formatCurrency(
          responseData.new_balance || originalBalance - paymentAmount,
        ),
        paymentId: responseData.payment_id || "Generated",
      });

      setCurrentInvoice((prev) => ({
        ...prev,
        balance: responseData.new_balance || originalBalance - paymentAmount,
      }));

      setFormData({
        payment_date: "",
        payment_description: "",
        payment_amount: "",
        client_id: currentInvoice.client_id,
        payment_type: "",
      });

      setTimeout(() => {
        fetchData();
        fetchPaymentHistory(currentInvoice.invoice_id);
      }, 1500);
    } catch (error) {
      console.error("Error updating invoice:", error);
      let errorMessage = error.message;

      if (
        error.message.includes("exceeds") ||
        error.message.includes("balance")
      ) {
        errorMessage = `Payment amount exceeds invoice balance`;
      } else if (
        error.message.includes("not found") ||
        error.message.includes("Invoice")
      ) {
        errorMessage = "Invoice not found";
      }

      setUpdateStatus((prev) => ({
        ...prev,
        success: false,
        message: errorMessage,
      }));
    } finally {
      setIsUpdating(false);
    }
  };

  // Helper functions
  const clientOptions = [
    { value: "all", label: "All Clients" },
    ...clients.map((client) => ({
      value: client.id,
      label: client.customer_name,
      client,
    })),
  ];

  const getInvoiceTypeName = (typeValue) => {
    if (typeof typeValue === "string") {
      return typeValue;
    }

    if (!Array.isArray(invoiceTypes)) return "Unknown";

    const typeStr = String(typeValue);
    const type = invoiceTypes.find(
      (t) =>
        String(t.invoice_type_number) === typeStr ||
        String(t.id) === typeStr ||
        String(t.value) === typeStr,
    );

    return type
      ? type.invoice_type_name || type.name || type.label || "Unknown"
      : "Unknown";
  };

  const getTypeClass = (invoiceType) => {
    if (typeof invoiceType === "string") {
      return invoiceType.toLowerCase().replace(/\s+/g, "-");
    }

    const typeMap = {
      1: "standard",
      2: "debit",
      3: "credit",
    };

    return typeMap[String(invoiceType)] || "unknown";
  };

  const formatCurrency = (amount) => {
    const numAmount = typeof amount === "string" ? parseFloat(amount) : amount;
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "LKR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(numAmount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      return format(new Date(dateString), "MMM d, yyyy");
    } catch {
      return dateString;
    }
  };

  // Loading state
  if (isLoading && !isRefreshing) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingSpinner} />
        <p>Loading invoices...</p>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className={styles.errorContainer}>
        <p className={styles.errorText}>Error: {error}</p>
        <button className={styles.retryButton} onClick={fetchData}>
          Retry
        </button>
      </div>
    );
  }

  // Main render
  return (
    <div className={styles.invoiceManagement}>
      <header className={styles.managementHeader}>
        <div className={styles.headerTitleRow}>
          <h1>Invoice Management</h1>
          <button
            className={styles.refreshButton}
            onClick={handleRefresh}
            disabled={isRefreshing}
          >
            <FiRefreshCw className={isRefreshing ? styles.refreshing : ""} />
          </button>
        </div>

        <div className={styles.headerControls}>
          <div className={styles.controlsLeft}>
            <button className={styles.createBtn} onClick={handleCreateInvoice}>
              <FiPlus /> Create Invoice
            </button>
            <button
              className={`${styles.completedToggle} ${
                showCompletedInvoices ? styles.active : ""
              }`}
              onClick={toggleCompletedInvoices}
            >
              <FiCheckCircle />{" "}
              {showCompletedInvoices
                ? "Show Active Invoices"
                : "Show Completed Invoices"}
            </button>
          </div>

          <div className={styles.controlsRight}>
            <div className={styles.tsoDropdown}>
              <Select
                options={[
                  { value: "all", label: "All TSOs" },
                  ...tsos.map((tso) => ({
                    value: tso.tso_id,
                    label: tso.tso_calling_name || tso.tso_name,
                  })),
                ]}
                value={selectedTsoFilter}
                onChange={setSelectedTsoFilter}
                placeholder="Filter by TSO"
                styles={customSelectStyles}
                isSearchable
                isClearable
              />
            </div>

            <div className={styles.monthPicker}>
              <FiCalendar className={styles.calendarIcon} />
              <DatePicker
                selected={dueMonthFilter}
                onChange={(date) => setDueMonthFilter(date)}
                dateFormat="MM/yyyy"
                showMonthYearPicker
                placeholderText="Filter by due month"
                isClearable
              />
            </div>

            <div className={styles.clientDropdown}>
              <Select
                options={clientOptions}
                value={selectedClient}
                onChange={setSelectedClient}
                placeholder="Filter by client"
                styles={customSelectStyles}
                isSearchable
                isClearable
              />
            </div>

            <div className={styles.searchBar}>
              <FiSearch className={styles.searchIcon} />
              <input
                type="text"
                placeholder="Search invoices..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        </div>
      </header>

      <div className={styles.totalsSection}>
        <div className={styles.totalItem}>
          <span className={styles.totalLabel}>Total Amount:</span>
          <span className={styles.totalValue}>
            {formatCurrency(getTotalAmount())}
          </span>
        </div>
        <div className={styles.totalItem}>
          <span className={styles.totalLabel}>Total Balance:</span>
          <span className={styles.totalValue}>
            {formatCurrency(getTotalBalance())}
          </span>
        </div>
      </div>

      <div className={styles.invoicesContainer}>
        <div className={styles.tableResponsiveWrapper}>
          {filteredInvoices.length > 0 ? (
            <>
              <div className={styles.tableInfo}>
                Showing {filteredInvoices.length} of{" "}
                {showCompletedInvoices
                  ? completedInvoices.length
                  : invoices.length}{" "}
                {showCompletedInvoices ? "completed" : "active"} invoices
              </div>
              <div className={styles.tableScrollWrapper}>
                <table className={styles.invoicesTable}>
                  <thead>
                    <tr>
                      <th className={styles.numberColumn}>Invoice</th>
                      <th className={styles.clientColumn}>Client</th>
                      <th className={styles.tsoColumn}>TSO</th>
                      <th className={styles.dateColumn}>Date</th>
                      <th className={styles.dateColumn}>Due Date</th>
                      <th className={styles.amountColumn}>Amount</th>
                      <th
                        className={`${styles.balanceColumn} ${
                          showCompletedInvoices
                            ? styles.reducedBalanceColumn
                            : ""
                        }`}
                      >
                        Balance
                      </th>
                      <th className={styles.typeColumn}>Type</th>
                      <th className={styles.categoryColumn}>Category</th>
                      <th
                        className={`${styles.actionsColumn} ${
                          showCompletedInvoices
                            ? styles.expandedActionsColumn
                            : ""
                        }`}
                      >
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredInvoices.map((invoice) => {
                      const typeClass = getTypeClass(invoice.invoice_type);
                      const isCompleted = parseFloat(invoice.balance) === 0;

                      return (
                        <tr
                          key={invoice.invoice_id}
                          onClick={() => handleShowPaymentHistory(invoice)}
                          className={`${styles.clickableRow} ${
                            isCompleted ? styles.completedRow : ""
                          }`}
                        >
                          <td className={styles.numberCell}>
                            <span className={styles.invoiceNumber}>
                              {invoice.invoice_num || "Not provided"}
                            </span>
                          </td>
                          <td className={styles.clientCell}>
                            <div className={styles.clientInfo}>
                              <div className={styles.clientName}>
                                {invoice.customer_name || "Unknown"}
                              </div>
                              {invoice.description && (
                                <div className={styles.invoiceDescription}>
                                  {invoice.description}
                                </div>
                              )}
                            </div>
                          </td>
                          <td className={styles.tsoCell}>
                            {invoice.tso_name || "-"}
                          </td>
                          <td className={styles.dateCell}>
                            {invoice.created_date
                              ? format(
                                  new Date(invoice.created_date),
                                  "MMM d, yyyy",
                                )
                              : "Not provided"}
                          </td>
                          <td className={styles.dateCell}>
                            {invoice.due_date
                              ? format(
                                  new Date(invoice.due_date),
                                  "MMM d, yyyy",
                                )
                              : "Not provided"}
                          </td>
                          <td className={styles.amountCell}>
                            {formatCurrency(invoice.amount)}
                          </td>
                          <td
                            className={`${styles.balanceCell} ${
                              isCompleted
                                ? styles.zeroBalance
                                : styles.blueBalance
                            } ${
                              showCompletedInvoices
                                ? styles.reducedBalanceCell
                                : ""
                            }`}
                          >
                            {formatCurrency(invoice.balance)}
                          </td>
                          <td className={styles.typeCell}>
                            <span
                              className={`${styles.typeBadge} ${
                                styles[typeClass] || styles.unknown
                              }`}
                            >
                              {getInvoiceTypeName(invoice.invoice_type)}
                            </span>
                          </td>
                          <td className={styles.categoryCell}>
                            {invoice.invoice_category || "Not provided"}
                          </td>
                          <td
                            className={`${styles.actionsCell} ${
                              showCompletedInvoices
                                ? styles.expandedActionsCell
                                : ""
                            }`}
                          >
                            {isCompleted ? (
                              <span className={styles.completedBadge}>
                                <FiCheckCircle /> Paid
                              </span>
                            ) : (
                              <button
                                className={styles.updateBtn}
                                onClick={(e) => handleUpdateInvoice(invoice, e)}
                                title="Edit invoice"
                              >
                                <FiEdit2 />
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <div className={styles.noResults}>
              <p>
                No {showCompletedInvoices ? "completed" : "active"} invoices
                found matching your criteria.
              </p>
              {(selectedClient ||
                selectedTsoFilter ||
                dueMonthFilter ||
                searchTerm) && (
                <button
                  className={styles.clearFilters}
                  onClick={() => {
                    setSelectedClient(null);
                    setSelectedTsoFilter(null);
                    setDueMonthFilter(null);
                    setSearchTerm("");
                  }}
                >
                  Clear filters
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Update Invoice Modal */}
      {showUpdateModal && currentInvoice && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContent}>
            <div className={styles.modalHeader}>
              <h2>Update Invoice ({currentInvoice.invoice_num})</h2>
              <button className={styles.closeButton} onClick={handleCloseModal}>
                <FiX />
              </button>
            </div>

            <div className={styles.modalBody}>
              <div className={styles.invoiceInfo}>
                <div className={styles.infoRow}>
                  <span className={styles.infoLabel}>Client:</span>
                  <span className={styles.infoValue}>
                    {currentInvoice.customer_name || "Unknown"}
                  </span>
                </div>
                <div className={styles.infoRow}>
                  <span className={styles.infoLabel}>Current Balance:</span>
                  <span className={styles.infoValue}>
                    {formatCurrency(currentInvoice.balance)}
                  </span>
                </div>
              </div>

              <form onSubmit={handleSubmit} className={styles.updateForm}>
                {updateStatus.message && (
                  <div
                    className={
                      updateStatus.success
                        ? styles.successMessage
                        : styles.errorMessage
                    }
                  >
                    {updateStatus.message}
                    {updateStatus.success && (
                      <div className={styles.paymentInfo}>
                        {updateStatus.paymentId && (
                          <p>
                            <strong>Payment ID:</strong>{" "}
                            {updateStatus.paymentId}
                          </p>
                        )}
                        <p>
                          <strong>New Balance:</strong>{" "}
                          {updateStatus.newBalance}
                        </p>
                      </div>
                    )}
                    {!updateStatus.success &&
                      updateStatus.message.includes("exceeds") && (
                        <div className={styles.currentBalanceWarning}>
                          Current balance:{" "}
                          {formatCurrency(currentInvoice.balance)}
                        </div>
                      )}
                  </div>
                )}

                <div className={styles.formGroup}>
                  <label htmlFor="payment_date">Payment Date*</label>
                  <input
                    type="date"
                    id="payment_date"
                    name="payment_date"
                    value={formData.payment_date}
                    onChange={handleInputChange}
                    className={formErrors.payment_date ? styles.errorInput : ""}
                    required
                  />
                  {formErrors.payment_date && (
                    <span className={styles.errorMessage}>
                      {formErrors.payment_date}
                    </span>
                  )}
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="payment_description">
                    Payment Description
                  </label>
                  <input
                    type="text"
                    id="payment_description"
                    name="payment_description"
                    value={formData.payment_description}
                    onChange={handleInputChange}
                    placeholder="Optional description"
                  />
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="payment_type">Payment Type*</label>
                  <Select
                    id="payment_type"
                    name="payment_type"
                    options={paymentTypeOptions}
                    value={paymentTypeOptions.find(
                      (option) => option.value === formData.payment_type,
                    )}
                    onChange={(selectedOption) =>
                      handleSelectChange("payment_type", selectedOption)
                    }
                    className={`${styles.paymentTypeSelect} ${
                      formErrors.payment_type ? styles.errorSelect : ""
                    }`}
                    placeholder="Select payment type"
                    styles={customSelectStyles}
                    required
                  />
                  {formErrors.payment_type && (
                    <span className={styles.errorMessage}>
                      {formErrors.payment_type}
                    </span>
                  )}
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="payment_amount">Payment Amount*</label>
                  <input
                    type="number"
                    id="payment_amount"
                    name="payment_amount"
                    value={formData.payment_amount}
                    onChange={handleInputChange}
                    className={
                      formErrors.payment_amount ? styles.errorInput : ""
                    }
                    step="0.01"
                    min="0.01"
                    max={currentInvoice.balance || currentInvoice.amount}
                    required
                  />
                  {formErrors.payment_amount && (
                    <span className={styles.errorMessage}>
                      {formErrors.payment_amount}
                    </span>
                  )}
                </div>

                <div className={styles.formActions}>
                  <button
                    type="button"
                    className={styles.cancelButton}
                    onClick={handleCloseModal}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className={styles.submitButton}
                    disabled={isUpdating}
                  >
                    {isUpdating ? "Updating..." : "Update Invoice"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Payment History Modal */}
      {showPaymentHistoryModal && currentInvoice && (
        <div className={styles.modalOverlay}>
          <div className={styles.paymentHistoryModal}>
            <div className={styles.modalHeader}>
              <h2>Payment History ({currentInvoice.invoice_num})</h2>
              <button className={styles.closeButton} onClick={handleCloseModal}>
                <FiX />
              </button>
            </div>

            <div className={styles.modalBody}>
              <div className={styles.invoiceSummary}>
                <div className={styles.summaryRow}>
                  <div className={styles.summaryItem}>
                    <span className={styles.summaryLabel}>Client:</span>
                    <span className={styles.summaryValue}>
                      {currentInvoice.customer_name || "Unknown"}
                    </span>
                  </div>
                  <div className={styles.summaryItem}>
                    <span className={styles.summaryLabel}>Description:</span>
                    <span className={styles.summaryValue}>
                      {currentInvoice.description || "-"}
                    </span>
                  </div>
                </div>
                <div className={styles.summaryRow}>
                  <div className={styles.summaryItem}>
                    <span className={styles.summaryLabel}>Created Date:</span>
                    <span className={styles.summaryValue}>
                      {formatDate(currentInvoice.created_date)}
                    </span>
                  </div>
                  <div className={styles.summaryItem}>
                    <span className={styles.summaryLabel}>Invoice Amount:</span>
                    <span className={styles.summaryValue}>
                      {formatCurrency(currentInvoice.amount)}
                    </span>
                  </div>
                </div>
              </div>

              {isLoadingPayments ? (
                <div className={styles.loadingPayments}>
                  <div className={styles.loadingSpinner} />
                  <p>Loading payment history...</p>
                </div>
              ) : paymentHistoryError ? (
                <div className={styles.paymentHistoryError}>
                  <p>Error loading payment history: {paymentHistoryError}</p>
                  <button
                    className={styles.retryButton}
                    onClick={() =>
                      fetchPaymentHistory(currentInvoice.invoice_id)
                    }
                  >
                    Retry
                  </button>
                </div>
              ) : paymentHistory.length > 0 ? (
                <div className={styles.paymentHistoryTableWrapper}>
                  <table className={styles.paymentHistoryTable}>
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Description</th>
                        <th>Type</th>
                        <th>Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paymentHistory.map((payment) => (
                        <tr key={payment.payment_id}>
                          <td>{formatDate(payment.payment_date)}</td>
                          <td>{payment.payment_description || "-"}</td>
                          <td>{payment.payment_type || "-"}</td>
                          <td className={styles.paymentAmount}>
                            {formatCurrency(payment.payment_amount)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className={styles.noPayments}>
                  <p>No payment history found for this invoice.</p>
                </div>
              )}

              <div className={styles.currentBalanceSection}>
                <div className={styles.balanceLabel}>Current Balance:</div>
                <div className={styles.balanceAmount}>
                  {formatCurrency(currentInvoice.balance)}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Custom styles for react-select component
const customSelectStyles = {
  control: (provided) => ({
    ...provided,
    minHeight: "40px",
    border: "1px solid #e2e8f0",
    borderRadius: "8px",
    boxShadow: "none",
    "&:hover": {
      borderColor: "#94a3b8",
    },
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? "#e0e7ff" : "white",
    color: state.isSelected ? "#1e40af" : "#1e293b",
    "&:hover": {
      backgroundColor: "#f1f5f9",
      color: "#1e40af",
    },
  }),
  menu: (provided) => ({
    ...provided,
    borderRadius: "8px",
    border: "1px solid #e2e8f0",
    boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
  }),
  indicatorSeparator: () => ({
    display: "none",
  }),
  dropdownIndicator: (provided) => ({
    ...provided,
    color: "#94a3b8",
    "&:hover": {
      color: "#64748b",
    },
  }),
};

export default InvoiceManagement;
